/**
 * Invicta Shared Utilities
 *
 * Single source of truth for utility functions previously duplicated
 * across multiple files (debounce, formatMoney, escapeHtml).
 *
 * Exposed on window.invictaUtils to follow the same global namespace
 * pattern used by the rest of the theme. Loaded after constants.js and
 * before any consumers in layout/theme.liquid.
 */
(function () {
  'use strict';

  var DEFAULT_MONEY_FORMAT = '£{{amount}}';

  /**
   * Returns a debounced version of fn that delays invocation until `wait`
   * milliseconds have elapsed since the last call.
   * Preserves `this` and arguments. Trailing-edge only.
   *
   * @param {Function} fn
   * @param {number} wait
   * @returns {Function}
   */
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /**
   * Format a price in pence/cents into a localised money string.
   * Prefers Shopify.formatMoney when available so the merchant's configured
   * format is applied. Falls back to a manual replacement that handles the
   * three common Liquid placeholders.
   *
   * Negative values are formatted with a leading minus on the amount.
   * Non-finite values (NaN, Infinity) fall back to a £0.00-equivalent string.
   *
   * @param {number} cents - Price in pence/cents
   * @param {string} [format] - Optional money format template
   * @returns {string}
   */
  function formatMoney(cents, format) {
    var fmt = format || DEFAULT_MONEY_FORMAT;
    var amountNumber = Number(cents);
    if (!isFinite(amountNumber)) amountNumber = 0;

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(amountNumber, fmt);
    }

    var negative = amountNumber < 0;
    var abs = Math.abs(amountNumber);
    var amount = (abs / 100).toFixed(2);
    var amountNoDecimals = String(Math.round(abs / 100));
    var amountComma = amount.replace('.', ',');

    var formatted = fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amountComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals);

    return negative ? '-' + formatted : formatted;
  }

  /**
   * Escape arbitrary text for safe insertion into innerHTML.
   * Uses a detached text node so the browser handles all relevant entities.
   * Returns an empty string for null/undefined.
   *
   * @param {*} str
   * @returns {string}
   */
  function escapeHtml(str) {
    if (str == null) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
  }

  window.invictaUtils = {
    debounce: debounce,
    formatMoney: formatMoney,
    escapeHtml: escapeHtml,
    showToast: showToast,
    DEFAULT_MONEY_FORMAT: DEFAULT_MONEY_FORMAT,
  };

  /**
   * Show a brief toast notification at the top of the viewport.
   * Used for ATC success/error feedback so the confirmation is visible
   * even when the user has scrolled away from the button (e.g. from the
   * sticky ATC) or the cart drawer takes a moment to slide in.
   *
   * Toasts auto-stack inside a single aria-live container so a screen
   * reader announces them once. Reduced-motion respected via CSS.
   *
   * @param {string} message
   * @param {Object} [opts]
   * @param {'success'|'error'|'info'} [opts.type='success']
   * @param {number} [opts.duration=2200] ms before auto-dismiss
   * @returns {HTMLElement|null}
   */
  function showToast(message, opts) {
    if (!message) return null;
    opts = opts || {};
    var type = opts.type === 'error' || opts.type === 'info' ? opts.type : 'success';
    /* Default duration scales with message length so screen-reader users
       (and tradies reading on a glare-y phone in the van) actually have
       time to consume long messages. Cap at 6s so it doesn't linger.
       Error toasts get a higher floor since the user usually needs to
       decide what to do next. */
    var baseFloor = type === 'error' ? 3500 : 2200;
    var lengthFloor = Math.min(6000, baseFloor + String(message).length * 45);
    var duration = typeof opts.duration === 'number' ? opts.duration : lengthFloor;

    var container = document.getElementById('inv-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'inv-toast-container';
      /* Container is polite by default; error toasts override per-toast
         via role="alert" so a failed ATC interrupts whatever the screen
         reader is currently saying. */
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('aria-atomic', 'true');
      document.body.appendChild(container);
    }

    var toast = document.createElement('div');
    toast.className = 'inv-toast inv-toast--' + type;
    if (type === 'error') {
      toast.setAttribute('role', 'alert');
    }

    var iconSvg;
    if (type === 'success') {
      iconSvg = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" focusable="false" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
    } else if (type === 'error') {
      iconSvg = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" focusable="false" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
    } else {
      iconSvg = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" focusable="false" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="12" y1="7" x2="12.01" y2="7"/></svg>';
    }
    /* Close button — lets users dismiss a toast early instead of
       waiting for the auto-dismiss timer. Particularly useful for the
       longer error toasts. The X icon is inline SVG; aria-label is set
       on the button itself. */
    var closeBtn =
      '<button type="button" class="inv-toast__close" aria-label="Dismiss notification">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" focusable="false" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' +
        '</svg>' +
      '</button>';
    toast.innerHTML = iconSvg + '<span class="inv-toast__msg">' + escapeHtml(message) + '</span>' + closeBtn;

    container.appendChild(toast);

    // Trigger entrance after the element is in the DOM so the transition runs
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    });

    var dismissTimer;
    var dismissed = false;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      clearTimeout(dismissTimer);
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }

    var btn = toast.querySelector('.inv-toast__close');
    if (btn) btn.addEventListener('click', dismiss);

    dismissTimer = setTimeout(dismiss, duration);

    return toast;
  }
})();
